import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Search, Download, GitMerge, ArrowLeftRight, TrendingUp, Zap, Clock } from 'lucide-react';
import API_BASE_URL from '../config/api';

const MatchingIncome = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [incomeData, setIncomeData] = useState([]);
    const [preview, setPreview] = useState(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/income_matching.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setIncomeData(result.data);
                setPreview(result.preview);
            }
        } catch (error) {
            console.error("Failed to fetch matching income", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatPV = (pv) => {
        return parseFloat(pv || 0).toFixed(2);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    const totalPayout = incomeData.reduce((acc, curr) => acc + parseFloat(curr.payout || 0), 0);

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-8 max-w-7xl mx-auto">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 font-fredoka">Matching Income Center</h1>
                    <p className="text-gray-500 text-sm mt-1 font-medium">Daily settlements from binary team volume</p>
                </div>

                {/* Potential Matching Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Potential Match Card */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-xl border-2 border-pink-50 relative overflow-hidden group col-span-1 lg:col-span-2">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-pink-50/50 rounded-bl-full -mr-24 -mt-24 transition-all group-hover:scale-110"></div>

                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-pink-600 rounded-2xl text-white shadow-lg shadow-pink-200">
                                <ArrowLeftRight size={28} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-gray-900 leading-tight">Cycle Potential</h2>
                                <p className="text-pink-600 font-black text-[10px] uppercase tracking-widest">Calculated Real-Time</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Left Active PV</p>
                                        <p className="text-2xl font-black text-indigo-600">{formatPV(preview?.effective_left_pv)}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Right Active PV</p>
                                        <p className="text-2xl font-black text-purple-600">{formatPV(preview?.effective_right_pv)}</p>
                                    </div>
                                </div>
                                <div className="p-4 bg-indigo-600 rounded-2xl text-white text-center shadow-lg transform group-hover:scale-[1.02] transition-transform">
                                    <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest mb-1">Estimated Matching Bonus</p>
                                    <p className="text-3xl font-black font-fredoka">{formatCurrency(preview?.matching_income)}</p>
                                    <p className="text-[10px] mt-1 font-bold opacity-50">Match: {formatPV(preview?.matching_pv)} PV × ₹40</p>
                                </div>
                            </div>

                            <div className="bg-amber-50 rounded-3xl p-6 border border-amber-100 h-full">
                                <h4 className="text-[10px] font-black text-amber-600 uppercase mb-4 flex items-center gap-2">
                                    <Clock size={14} /> Next Cycle Settlements
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-amber-800/60 font-bold uppercase">Cycle Limit</span>
                                        <span className="font-black text-amber-900">₹ 5,000.00</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-amber-800/60 font-bold uppercase">Status</span>
                                        <span className="font-black text-emerald-600 flex items-center gap-1">
                                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></div>
                                            LIVE
                                        </span>
                                    </div>
                                    <div className="pt-3 border-t border-amber-200/50">
                                        <p className="text-[10px] text-amber-800/70 font-medium leading-relaxed">
                                            Volume is calculated every 12 hours. Any unmatched PV carries forward automatically.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lifetime Total */}
                    <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-[2.5rem] p-8 text-white shadow-xl relative overflow-hidden group">
                        <TrendingUp className="absolute -right-8 -bottom-8 w-48 h-48 text-white/10" />
                        <div className="relative z-10 flex flex-col justify-between h-full">
                            <div>
                                <p className="text-indigo-200 text-xs font-black uppercase tracking-widest mb-2">Lifetime Matching</p>
                                <h2 className="text-4xl font-black font-fredoka leading-tight">
                                    {formatCurrency(totalPayout)}
                                </h2>
                            </div>
                            <div className="mt-8">
                                <div className="bg-white/10 rounded-2xl p-4 border border-white/10 backdrop-blur-md">
                                    <div className="flex justify-between text-[10px] font-bold opacity-60 uppercase mb-2">
                                        <span>Total Settlements</span>
                                        <span>{incomeData.length}</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-white/10 rounded-full">
                                        <div className="h-full bg-white rounded-full w-[100%]"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* History Table */}
                <div className="bg-white rounded-[2.5rem] shadow-xl border-2 border-slate-50 overflow-hidden">
                    <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                        <h3 className="text-xl font-black text-gray-900">Settlement Logs</h3>

                        <div className="flex w-full md:w-auto gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search by Date..."
                                    className="w-full md:w-64 pl-10 pr-4 py-3 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button className="p-3 bg-gray-50 text-gray-400 rounded-2xl hover:bg-gray-100 transition-colors">
                                <Download size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50/50 text-[10px] uppercase font-black tracking-widest text-gray-400">
                                <tr>
                                    <th className="px-8 py-5">Settlement Date</th>
                                    <th className="px-8 py-5">Cycle Intake (L:R)</th>
                                    <th className="px-8 py-5 text-center">Matching Business</th>
                                    <th className="px-8 py-5 text-right">Income Generated</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {incomeData.filter(item => item.date.includes(searchTerm)).length > 0 ? (
                                    incomeData.filter(item => item.date.includes(searchTerm)).map((item) => (
                                        <tr key={item.id} className="group hover:bg-indigo-50/30 transition-colors">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-pink-50 rounded-lg text-pink-600 flex-icon-fix">
                                                        <GitMerge size={16} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-gray-900">{item.date}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase">Status: {item.status}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex gap-4">
                                                    <div>
                                                        <p className="text-[8px] font-black opacity-30 uppercase tracking-tighter">Left</p>
                                                        <p className="text-xs font-black text-indigo-600">{formatPV(item.leftBusiness)} PV</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[8px] font-black opacity-30 uppercase tracking-tighter">Right</p>
                                                        <p className="text-xs font-black text-purple-600">{formatPV(item.rightBusiness)} PV</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <p className="text-sm font-black text-gray-700">{formatPV(item.matchingAmount)} PV</p>
                                                <p className="text-[10px] font-bold text-gray-400 capitalize">Full depth match</p>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <p className="text-lg font-black text-emerald-600 font-fredoka">+ {formatCurrency(item.payout)}</p>
                                                <p className="text-[9px] font-bold text-gray-400">Paid to Wallet</p>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4">
                                                <div className="p-4 bg-gray-50 rounded-full">
                                                    <Zap size={40} className="text-gray-200" />
                                                </div>
                                                <p className="text-gray-400 font-black uppercase tracking-widest text-xs">No matching settlements found</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default MatchingIncome;


